import React, { forwardRef } from "react";

import PropTypes from "prop-types";
import { FormattedMessage, MessageDescriptor } from "react-intl";

import { Inline, Pressable, Text, xcss } from "@atlaskit/primitives-next";
import { token } from "@atlaskit/tokens";

interface TabPillProps {
    /** Unique ID for the tab element */
    id: string;
    /** ID of the associated tabpanel for aria-controls */
    tabPanelId: string;
    icon: React.ReactNode;
    text: MessageDescriptor;
    onSelect: () => void;
    isSelected?: boolean;
    onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}

const tabPillCommonStyle = xcss({
    "borderRadius": "90px",
    "padding": "unset",
    "border": `1px solid ${token("color.background.neutral")}`,
    ":focus": {
        outlineColor: "color.border.focused",
        outlineWidth: "border.width.focused",
        outlineStyle: "solid"
    }
});

const tabPillSelectedStyle = xcss({
    "borderWidth": "border.width",
    "borderStyle": "solid",
    "borderColor": "color.border.selected",
    "color": "color.text.brand",
    "backgroundColor": "color.background.selected",
    ":hover": {
        backgroundColor: "color.background.selected.hovered"
    }
});

const tabPillDefaultStyle = xcss({
    "color": "color.text",
    "backgroundColor": "color.background.neutral",
    ":hover": {
        backgroundColor: "color.background.neutral.hovered"
    }
});

const tabPillContentStyle = xcss({
    height: "32px",
    paddingBlock: "space.075",
    paddingInline: "space.150"
});

export const TabPill = forwardRef<HTMLButtonElement, TabPillProps>(
    (
        { id, tabPanelId, icon, text, isSelected = false, onSelect, onKeyDown },
        ref
    ) => {
        return (
            <Pressable
                ref={ref}
                id={id}
                xcss={[
                    tabPillCommonStyle,
                    isSelected ? tabPillSelectedStyle : tabPillDefaultStyle
                ]}
                onClick={onSelect}
                onKeyDown={onKeyDown}
                role="tab"
                aria-selected={isSelected}
                aria-controls={tabPanelId}
                tabIndex={isSelected ? 0 : -1}
            >
                <Inline
                    alignBlock="center"
                    xcss={tabPillContentStyle}
                    space="space.050"
                >
                    {icon}
                    <Text
                        color={isSelected ? "color.text.brand" : "color.text"}
                    >
                        <FormattedMessage {...text} />
                    </Text>
                </Inline>
            </Pressable>
        );
    }
);

TabPill.displayName = "TabPill";

TabPill.propTypes = {
    id: PropTypes.string.isRequired,
    tabPanelId: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    text: PropTypes.object.isRequired,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func
};
